import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { ApiService, AppointmentDataService, ConfirmService, detectICDVersion, DirectivesModule } from '@mmx/shared'
import { SatPopover, SatPopoverModule } from '@ncstate/sat-popover'
import { TranslateModule } from '@ngx-translate/core'
import { union } from 'lodash'
import { filter, of, Subject, switchMap, take, takeUntil } from 'rxjs'

import { DiagnosisCodeChipsSelectorComponent } from "../diagnosis-code-chips-selector/diagnosis-code-chips-selector.component"

@Component({
  selector: 'mmx-diagnosis-codes-card',
  templateUrl: './diagnosis-code-card.component.html',
  styleUrls: ['./diagnosis-code-card.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatProgressBarModule,
    FlexLayoutModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatTableModule,
    TranslateModule,
    DirectivesModule,
    ReactiveFormsModule,
    DiagnosisCodeChipsSelectorComponent,
    SatPopoverModule,
  ],
})
export class DiagnosisCodeCardsComponent implements OnDestroy {
    @Input() public editable = true

    displayedColumns = [
      'code',
      'type',
      // 'description',
      'actions',
    ]

    formGroup = this.fb.group({
      codes: this.fb.control(null, [Validators.required]),
    })

    formError = ''

    isSaving = false

    private onDestroy$: Subject<void> = new Subject<void>()

    constructor(
      private fb: FormBuilder,
      public data: AppointmentDataService,
      public restService: ApiService,
      private confirmService: ConfirmService,
    ) {
    }

    // getDescription(code: string) {
    //   // TODO: return description
    // }

    getCodeType(code: string) {
      return detectICDVersion(code)
    }

    getDataSource (data: string[]) {
      return new MatTableDataSource(data)
    }

    openForm(popover: SatPopover) {
      this.formError = ''
      this.formGroup.reset()
      popover.open()
    }

    addCodes(popup: SatPopover) {
      this.formError = ''
      this.isSaving = true

      setTimeout(() => {
        if (!this.formGroup.value.codes?.length) {
          this.formError = 'patient-details.diagnosis-code-card.invalid-code'
          this.isSaving = false
          return
        }

        this.data.appointment$.pipe(
          take(1),
          switchMap((appointment) => {
            if (appointment) {
              const originalLength = appointment.diagnosisCodes?.length || 0
              appointment.diagnosisCodes = union(appointment.diagnosisCodes || [], this.formGroup.value.codes || [])
              if (appointment.diagnosisCodes.length > originalLength) {
                return this.restService.updateAppointment(appointment)
              }
            }
            return of(null)
          }),
          takeUntil(this.onDestroy$),
        )
          .subscribe((appt) => {
            this.isSaving = false
            if (appt) {
              this.data.appointmentUpdated$.next()
            }
            popup.close()
          })
      }, 1)
    }

    deleteDiagnosisCode(existingCode: string) {
      return this.confirmService
        .confirm(
          `Remove ${existingCode} from this appointment?`,
        )
        .pipe(
          filter((isConfirmed) => isConfirmed), // Only proceed if the user confirmed to resend
          switchMap(() => this.data.appointment$ || of(null)),
          take(1),
          switchMap((appointment) => {
            if (appointment) {
              appointment.diagnosisCodes = appointment.diagnosisCodes.filter(code => code !== existingCode)
              return this.restService.updateAppointment(appointment)
            } else {
              return of(null)
            }
          }),
        )
        .subscribe((appt) => {
          if (appt) {
            this.data.appointmentUpdated$.next()
          }
        })
    }

    ngOnDestroy(): void {
      this.onDestroy$.next()
      this.onDestroy$.complete()
    }
}